import { autoinject, bindable, Disposable } from "aurelia-framework";
import { LookupClientService } from "../../../services/lookups/lookup-client.service";
import { LookupValueRequest, LookupValue, LookupValueResponse } from "../../../models/lookups/lookup.models";
import { QueryFilter, DataType } from "../../../models/queries/queries.models";
import { EventAggregator } from "aurelia-event-aggregator";
import { Messages } from "../../../configuration/event-messages.constants";

@autoinject
export class MultiCheckedListSelector {

    // Document click event subscription
    private _subscriptionToDocumentClickEvent: Disposable;

    // Is the current component open
    private _isOpen: boolean;

    // Store initial/previous state of multiLookups
    public previousMultiLookups: MultiLookupModel[] = [];

    // MultiLookup models
    @bindable
    public multiLookups: MultiLookupModel[] = [];

    // Is single select enabled
    @bindable
    public isSingleSelect: boolean = false;

    constructor(private lookupsClientService: LookupClientService, private element: Element, private eventAggregator: EventAggregator) { }

    attached() {
        setTimeout(() => {
            this._isOpen = true;
            this.previousMultiLookups = this.multiLookups;
            this._subscriptionToDocumentClickEvent = this.eventAggregator.subscribe(Messages.documentClick, this.closePopup.bind(this));
        });
    }

    detached() {
        if (this._subscriptionToDocumentClickEvent)
            this._subscriptionToDocumentClickEvent.dispose();
    }

    /**
     * Close component popup (fyi, this component will most likely make part of a larger component that disappears on document click)
     * @param event
     */
    closePopup(event: any) {
        if (event?.source == 'document' && this._isOpen) {
            this._isOpen = false;
            this.applyFilters(true, true);
        }
    }

    /**
     * 'multiLookups' value changed handler
     */
    multiLookupsChanged() {
        if (this.multiLookups?.length > 0) {
            this.multiLookups.forEach((multiLookup: MultiLookupModel) => {
                this.lookupsClientService.getLookup(multiLookup.request)
                    .then((lookupValue: LookupValueResponse) => {
                        if (!multiLookup.values) multiLookup.values = [];

                        const selectedIds = multiLookup.values.filter(x => x.isSelected).map(x => x.id);
                        const parentSelectedIds = multiLookup.values.filter(x => x.isParentTicked).map(x => x.id);

                        multiLookup.filteredValues = multiLookup.values = lookupValue.activeValues;

                        multiLookup.values.filter(x => selectedIds.indexOf(x.id) > -1).forEach(x => x.isSelected = true);
                        multiLookup.values.filter(x => parentSelectedIds.indexOf(x.id) > -1).forEach(x => x.isParentTicked = true);

                    });
                multiLookup.isShowing = false;
            });

            this.multiLookups[0].isShowing = true;
        }
    }

    switchItemSelection(currentLookupItem: LookupValue, multiLookupModel: MultiLookupModel) {
        if (this.isSingleSelect) {
            this.multiLookups.forEach((multiLookup: MultiLookupModel) => {
                multiLookup.values.forEach((lookupValue: LookupValue) => {
                    lookupValue.isSelected = lookupValue ==currentLookupItem;
                });
            });
        } else {
            const currentIndex = this.multiLookups.indexOf(multiLookupModel);
            const nextMultiLookup = this.multiLookups[currentIndex + 1];

            if (nextMultiLookup) {
                const nextLookupValues = nextMultiLookup.values.filter((lookupValue: LookupValue) =>
                    lookupValue.additionalData[nextMultiLookup.joininPropertyName] == currentLookupItem.id
                );

                nextLookupValues.forEach((nextLookupValue: LookupValue) => {
                    nextLookupValue.isSelected = nextLookupValue.isParentTicked = currentLookupItem.isSelected;
                    this.switchItemSelection(nextLookupValue, nextMultiLookup);
                });
            }
        }
    }

    openChildren(item: LookupValue, multiLookupModel: MultiLookupModel) {
        const currentIndex = this.multiLookups.indexOf(multiLookupModel);

        //make sure all following panes are closed
        for (let index = currentIndex + 1; index < this.multiLookups.length - (currentIndex - 1); index++) {
            if (this.multiLookups[index]) {
                this.multiLookups[index].isShowing = false;
            }
        }

        const nextMultiLookupModel = this.multiLookups[currentIndex + 1];

        if (nextMultiLookupModel) {
            nextMultiLookupModel.title = item.name;
            nextMultiLookupModel.isShowing = !nextMultiLookupModel.isShowing;

            this.filterMultiLookup(nextMultiLookupModel, item.id);
        }
    }

    public clear() {
        this.multiLookups.forEach(x => {
            x.values.forEach(x => x.isSelected = false);
        });

        setTimeout(() => {
            this.applyFilters(false);
        }, 120);
    }

    public applyFilters(closeFilter: boolean = true, reset: boolean = false) {
        if (reset) this.multiLookups = this.previousMultiLookups;

        const changeEvent: CustomEvent = new CustomEvent("apply-filters", { bubbles: true, detail: { closeFilter: closeFilter, filters: this.multiLookups } });
        this.element.dispatchEvent(changeEvent);
    }

    public back(multiLookup: MultiLookupModel) {
        multiLookup.isShowing = false;
    }

    public filterMultiLookup(multiLookup: MultiLookupModel, filterId: number) {

        if (!multiLookup || !multiLookup.values) return [];

        multiLookup.filteredValues = multiLookup.values.filter(x => !filterId || x.additionalData[multiLookup.joininPropertyName] == filterId);
    }
}

export interface MultiLookupModel {
    request: LookupValueRequest;
    values?: LookupValue[];
    filteredValues?: LookupValue[];
    isShowing: boolean;
    propertyName: string;
    joininPropertyName: string;
    isMain: boolean;
    title?: string;
}

export class RightShipMultiLookupModels {

    static multiLookupModels: { [name: string]: MultiLookupModel[]; } = {
        "RS_VesselTypes": [
            {
                isShowing: true,
                request: new LookupValueRequest("RS_VesselTypeGroups"),
                propertyName: 'vesselTypeGroupId',
                joininPropertyName: '',
                isMain: false,
                title: 'Vessel Type Group'
            },
            {
                isShowing: false,
                request: new LookupValueRequest("RS_VesselTypes"),
                propertyName: 'shipTypeId',
                joininPropertyName: 'vesselTypeGroupId',
                isMain: true
            }
        ],
        "RS_VesselStatuses": [
            {
                isShowing: true,
                request: new LookupValueRequest("RS_VesselStatusGroups"),
                propertyName: 'vesselStatusGroupId',
                joininPropertyName: '',
                isMain: false,
                title: 'Vessel Status Group'
            },
            {
                isShowing: false,
                request: new LookupValueRequest("RS_VesselStatuses"),
                propertyName: 'shipStatusId',
                joininPropertyName: 'vesselStatusGroupId',
                isMain: true
            }
        ]
    };

    public static getMultiLookupModel(name: string) {
        return this.multiLookupModels[name];
    }

    //set selected ids at level and below,
    public static setSelectedInMultiLookupByIds(name: string, lookupService: LookupClientService, selectedIds: number[], query: string = null, index: number = null) {
        return new Promise<{
            filters: QueryFilter[];
            filterDisplayText: string;
            filterDescription: string;
        }>((resolve, reject) => {
            const lookupResult: { lookup, index } = this.getLookupAtLevel(index, query, name);
            const lookup = lookupResult.lookup;
            index = lookupResult.index;

            this.setMultiLookupValues(lookup, name, lookupService).then(() => {
                resolve(this.setDefaultSelected(name, lookup, selectedIds, index));
            })
                .catch(e => { reject(e); });
        });
    }

    //set selected values at level and below
    public static setSelectedInMultiLookup(name: string, lookupService: LookupClientService, selectedNames: string[], query: string = null, index: number = null) {
        return new Promise<{
            filters: QueryFilter[];
            filterDisplayText: string;
            filterDescription: string;
        }>((resolve, reject) => {
            const lookupResult: { lookup, index } = this.getLookupAtLevel(index, query, name);
            const lookup = lookupResult.lookup;
            index = lookupResult.index;

            let selectedIds: number[] = []

            const handleLookupValueResponse = (response: LookupValueResponse) => {
                if (response.lookupValueQuery == lookup.request.lookupValueQuery) {
                    selectedIds = response.activeValues.filter(v => v.name && v.name.trim() != '' && selectedNames.indexOf(v.name.toLowerCase().trim()) > -1).map(v => v.id);
                }
            }

            this.setMultiLookupValues(lookup, name, lookupService, handleLookupValueResponse).then(() => {
                resolve(this.setDefaultSelected(name, lookup, selectedIds, index));
            })
                .catch(e => { reject(e); });
        });
    }

    private static setMultiLookupValues(lookup: any, name: string, lookupService: LookupClientService, handleLookupValueResponse?: (response: LookupValueResponse) => void) {
        return new Promise<void>((resolve) => {
            if (!lookup.values || lookup.values.length == 0) {
                const promises = this.multiLookupModels[name].map(x => lookupService.getLookup(x.request));
                Promise.all(promises).then(x => {
                    x.forEach(q => {
                        this.multiLookupModels[name].filter(l => q.lookupValueQuery == l.request.lookupValueQuery).forEach(l => {
                            l.values = l.filteredValues = q.activeValues;
                        });
                        if (handleLookupValueResponse)
                            handleLookupValueResponse(q);
                    });
                    resolve();
                });
            }
            else
                resolve();
        });
    }

    //get Lookup At Level by index or by query
    private static getLookupAtLevel(index: number, query: string, name: string) {
        if ((index < -1 || index == null) && (!query || query.trim() == "")) {
            throw new Error("lookup index or query required!");
        }
        if (query && query.trim() != '') {
            const lookupsWithQuery = this.multiLookupModels[name].filter(x => x.request.lookupValueQuery == query);
            if (lookupsWithQuery && lookupsWithQuery.length > 0)
                index = this.multiLookupModels[name].indexOf(lookupsWithQuery[0]);
            else
                throw new Error("query not found!");
        }
        const lookup = this.multiLookupModels[name][index];
        return { lookup, index };
    }

    private static setDefaultSelected(name: string, lookup: MultiLookupModel, selectedIds: number[], index: number) {
        lookup.values.filter(x => selectedIds.indexOf(x.id) > -1).forEach(x => {
            x.isSelected = true;
            for (let i = index; i < this.multiLookupModels[name].length; i++) {
                const subLookup = this.multiLookupModels[name][i];
                if (subLookup.values)
                    subLookup.values.filter(v => v.additionalData[subLookup.joininPropertyName] == x.id).forEach(vs => {
                        vs.isSelected = true;
                        vs.isParentTicked = true;
                    });
            }
        });

        return this.getMultiLookupFilters(name, "");
    }

    public static getMultiLookupFilters(name: string, displayText: string, multiLookups: MultiLookupModel[] = null) {
        multiLookups = multiLookups && multiLookups.length > 0 ? multiLookups : this.multiLookupModels[name];
        const filters: QueryFilter[] = [];
        let selectedItems = 0

        let subText = '';

        let subTextCount = 0;

        multiLookups.forEach((multiLookup) => {

            if (multiLookup.values && multiLookup.values.filter(x => (<any>x).isSelected).length) {

                const selectedValues = multiLookup.values.filter(x => (<any>x).isSelected && !(<any>x).isParentTicked);

                selectedValues.forEach(x => {

                    if (subTextCount > 0 && subTextCount < 2) {
                        subText += ', ';
                    }

                    if (subTextCount < 2) {
                        subText += x.name;
                    }

                    subTextCount++;
                });

                const selectedIds = selectedValues.map(x => x.id);
                const filter = QueryFilter.getQuery(multiLookup.propertyName, selectedIds, '{0} in ({1})', DataType.Number, (!filters || filters.length == 0), false, name + '_multi');

                filter.filterOperator.requiresInterpolation = true;

                filters.push(filter);

                selectedItems += selectedIds.length;
            }

            multiLookup.isShowing = false;
        });

        if (subTextCount > 2) {
            subText += " +" + (subTextCount - 2) + " more";
        }

        let filterDescription = subText;

        multiLookups[0].isShowing = true;

        let filterDisplayText = "";

        if (selectedItems) {
            filterDisplayText = displayText + ' (' + selectedItems + ')';
        }
        else {
            filterDisplayText = displayText;
            filterDescription = "";
        }

        return {
            filters: filters,
            filterDisplayText: filterDisplayText,
            filterDescription: filterDescription
        }
    }
}